import { useRef, useState } from 'react';
import {
  Check,
  Images,
  Info,
  Loader2,
  Minus,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from 'react-toastify';
import type {
  CreativePreview,
  ManualCampaignForm,
} from '@/features/campaigns/types/campaign';
import {
  MAX_AD_DURATION,
  MIN_AD_DURATION,
} from '@/features/campaigns/constants/campaignOptions';
import { useManualAlertOptional } from '@/features/campaigns/components/ManualBuilder/ManualAlert';
import { useUploadSingleFile } from '@/shared/lib/upload/useUploadSingleFile';
import { useDeleteFile } from '@/shared/lib/upload/useDeleteFile';
import {
  forgetContentPreview,
  getCachedContentPreview,
  rememberContentPreview,
} from '@/features/campaigns/utils/contentPreviewCache';
import { resolveCreativePreview } from '@/features/campaigns/utils/resolveCreativePreview';
import { ContentPreviewMedia } from '@/features/campaigns/components/ContentPreviewMedia';
import { GalleryPickerModal } from '@/features/campaigns/components/GalleryPickerModal';
import {
  firstGalleryMedia,
  galleryItemId,
  type GalleryItem,
  type GalleryMediaEntry,
  type GalleryMediaFile,
} from '@/features/gallery/types/gallery';

type CreativeUploadProps = {
  campaignData: ManualCampaignForm;
  setCampaignData?: React.Dispatch<React.SetStateAction<ManualCampaignForm>>;
  creativePreview: CreativePreview;
  setCreativePreview: React.Dispatch<React.SetStateAction<CreativePreview>>;
  error?: string;
  clearError?: (field: string) => void;
  hideDuration?: boolean;
  compact?: boolean;
  hidePreviewMedia?: boolean;
  forcedOrientation?: string;
  enableGallery?: boolean;
  onUploaded?: (item: {
    mediaId: string;
    orientation: string;
    mediaUrl?: string;
    mediaType?: string;
  }) => void;
  /** Called after the user removes the current creative (device or gallery). */
  onCleared?: () => void;
  /** True while a device upload is in progress. */
  onBusyChange?: (busy: boolean) => void;
};

const DURATION_PRESETS = [10, 15, 30, 60] as const;

function readVideoDuration(file: File) {
  return new Promise<number>((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = url;
    video.onloadedmetadata = () => {
      const d = Math.floor(video.duration || 0);
      URL.revokeObjectURL(url);
      resolve(d);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(0);
    };
  });
}

function isFileDrag(e: React.DragEvent) {
  return Array.from(e.dataTransfer.types).includes('Files');
}

function mediaTypeOf(media?: GalleryMediaFile | null) {
  return media?.mediaType || media?.type || media?.mimeType || '';
}

function mediaDocumentId(
  media: GalleryMediaFile | null | undefined,
  entry?: GalleryMediaEntry,
) {
  const fromMedia =
    (media as { _id?: string; mediaId?: string } | null | undefined)?._id ||
    (media as { mediaId?: string } | null | undefined)?.mediaId;
  if (fromMedia) return String(fromMedia);
  if (typeof entry?.mediaId === 'string' && entry.mediaId) return entry.mediaId;
  return '';
}

function pickGalleryEntry(item: GalleryItem, forcedOrientation?: string) {
  const entries = Array.isArray(item.mediaIds) ? item.mediaIds : [];
  if (forcedOrientation) {
    const wanted = forcedOrientation.toUpperCase();
    const match = entries.find(
      (e) => String(e.orientation || '').toUpperCase() === wanted,
    );
    if (match) return match;
  }
  return entries[0];
}

function clampDuration(n: number) {
  return Math.min(Math.max(n, MIN_AD_DURATION), MAX_AD_DURATION);
}

export function CreativeUpload({
  campaignData,
  setCampaignData,
  creativePreview,
  setCreativePreview,
  error,
  clearError,
  hideDuration = false,
  compact = false,
  hidePreviewMedia = false,
  forcedOrientation,
  enableGallery = true,
  onUploaded,
  onCleared,
  onBusyChange,
}: CreativeUploadProps) {
  const alert = useManualAlertOptional();
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const lastAppliedMediaRef = useRef<{
    mediaId?: string;
    fromGallery: boolean;
  }>({ fromGallery: false });
  const upload = useUploadSingleFile();
  const deleteFile = useDeleteFile();
  const [dragging, setDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadLabel, setUploadLabel] = useState('Uploading…');
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [showCreativeFormatInfo, setShowCreativeFormatInfo] = useState(false);

  const busy = isUploading || upload.isPending;

  const notifyError = (title: string, description: string) => {
    if (alert) {
      alert.error(title, description);
      return;
    }
    toast.error(description);
  };

  const notifySuccess = (title: string, description: string) => {
    if (alert) {
      alert.success(title, description);
      return;
    }
    toast.success(description);
  };

  /** Best-effort delete for device uploads only — never gallery selections. */
  const deleteDeviceMediaIfNeeded = (opts?: {
    mediaId?: string;
    fromGallery?: boolean;
  }) => {
    const id = opts?.mediaId?.trim();
    if (!id || opts?.fromGallery) return;
    void deleteFile.mutateAsync({ mediaId: id }).catch(() => {});
  };

  const resolveCurrentMedia = () => {
    const fromRef = lastAppliedMediaRef.current;
    if (fromRef.mediaId) return fromRef;
    const mediaId = campaignData.mediaIds?.[0]?.mediaId;
    const fromGallery = Boolean(
      campaignData.galleryId || campaignData.mediaIds?.[0]?.galleryId,
    );
    return { mediaId, fromGallery };
  };

  const clear = () => {
    const current = resolveCurrentMedia();
    deleteDeviceMediaIfNeeded(current);
    forgetContentPreview(current.mediaId);
    lastAppliedMediaRef.current = { fromGallery: false };
    if (creativePreview.url?.startsWith('blob:')) {
      URL.revokeObjectURL(creativePreview.url);
    }
    setCreativePreview({ url: '', type: '' });
    setCampaignData?.((prev) => ({
      ...prev,
      mediaIds: undefined,
      galleryId: undefined,
      mediaType: undefined,
      mediaDuration: undefined,
      mediaUrl: undefined,
    }));
    onCleared?.();
    if (inputRef.current) inputRef.current.value = '';
  };

  const applyMedia = ({
    mediaId,
    orientation,
    mediaUrl,
    mediaType,
    mediaDuration,
    galleryId,
  }: {
    mediaId: string;
    orientation: string;
    mediaUrl: string;
    mediaType: string;
    mediaDuration?: string;
    galleryId?: string;
  }) => {
    const item = {
      mediaId,
      orientation,
      mediaUrl,
      mediaType,
      ...(galleryId ? { galleryId } : {}),
    };
    lastAppliedMediaRef.current = {
      mediaId,
      fromGallery: Boolean(galleryId),
    };
    clearError?.('mediaId');
    clearError?.('customDuration');
    onUploaded?.(item);
    const cached = getCachedContentPreview(mediaId);
    setCreativePreview({
      url: cached?.blobUrl || mediaUrl,
      type: cached?.type || mediaType,
    });
    setCampaignData?.((prev) => ({
      ...prev,
      mediaIds: [item],
      galleryId,
      mediaType,
      mediaDuration: mediaDuration || prev.mediaDuration,
      mediaUrl,
      customDuration:
        mediaType.startsWith('video') && mediaDuration
          ? String(
              Math.min(
                Math.max(Number(mediaDuration), MIN_AD_DURATION),
                MAX_AD_DURATION,
              ),
            )
          : prev.customDuration || '30',
    }));
  };

  const handleGallerySelect = (item: GalleryItem) => {
    const entry = pickGalleryEntry(item, forcedOrientation);
    const media = entry?.media || firstGalleryMedia(item);
    const mediaId = mediaDocumentId(media, entry);
    const mediaUrl = media?.url || '';
    if (!mediaId || !mediaUrl) {
      notifyError(
        'Creative unavailable',
        'This gallery item has no usable media.',
      );
      return;
    }

    const previous = resolveCurrentMedia();
    deleteDeviceMediaIfNeeded(previous);
    if (previous.mediaId && !previous.fromGallery) {
      forgetContentPreview(previous.mediaId);
      lastAppliedMediaRef.current = { fromGallery: false };
    }

    const mediaType = mediaTypeOf(media) || 'image/*';
    const orientation =
      forcedOrientation || entry?.orientation || 'LANDSCAPE';
    const durationRaw = (media as { duration?: number | string } | null)
      ?.duration;
    const mediaDuration = durationRaw
      ? String(durationRaw)
      : mediaType.startsWith('image')
        ? '30'
        : undefined;

    applyMedia({
      mediaId,
      orientation: String(orientation).toUpperCase(),
      mediaUrl,
      mediaType,
      mediaDuration,
      galleryId: galleryItemId(item) || undefined,
    });
    notifySuccess('Creative selected', 'Gallery creative added to this campaign.');
  };

  const handleFile = async (file: File | undefined) => {
    if (!file || busy) return;
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    if (!isVideo && !isImage) {
      notifyError('Unsupported file', 'Please upload an image or video.');
      return;
    }
    if (isVideo && file.size > 100 * 1024 * 1024) {
      notifyError('File too large', 'Video must be under 100MB.');
      return;
    }
    if (isImage && file.size > 25 * 1024 * 1024) {
      notifyError('File too large', 'Image must be under 25MB.');
      return;
    }

    const previous = resolveCurrentMedia();
    deleteDeviceMediaIfNeeded(previous);
    if (previous.mediaId) {
      forgetContentPreview(previous.mediaId);
      lastAppliedMediaRef.current = { fromGallery: false };
    }

    if (creativePreview.url?.startsWith('blob:')) {
      URL.revokeObjectURL(creativePreview.url);
    }
    setCreativePreview({ url: '', type: '' });
    setIsUploading(true);
    onBusyChange?.(true);
    setUploadLabel(isVideo ? 'Uploading video…' : 'Uploading image…');

    let mediaDuration = '';
    try {
      if (isVideo) {
        setUploadLabel('Reading video…');
        const dur = await readVideoDuration(file);
        mediaDuration = String(dur);
        setUploadLabel('Uploading video…');
      }

      const result = await upload.mutateAsync({
        file,
        moduleName: 'campaign',
      });
      const mediaId = result?.data?.mediaId as string | undefined;
      if (!mediaId) {
        notifyError('Upload failed', 'Could not save this file. Try again.');
        return;
      }
      const durableUrl =
        (result?.data?.url as string | undefined) ||
        (result?.data?.mediaUrl as string | undefined) ||
        '';
      const rawOrientation = result?.dimensions?.orientation;
      const orientation =
        forcedOrientation ||
        (rawOrientation &&
        ['PORTRAIT', 'LANDSCAPE', 'SQUARE'].includes(
          rawOrientation.toUpperCase(),
        )
          ? rawOrientation.toUpperCase()
          : 'LANDSCAPE');

      const previewUrl = rememberContentPreview(mediaId, file);
      applyMedia({
        mediaId,
        orientation,
        mediaUrl: durableUrl || previewUrl,
        mediaType: file.type,
        mediaDuration: mediaDuration || undefined,
        galleryId: undefined,
      });
      notifySuccess('Creative uploaded', 'Your content is ready for this campaign.');
    } catch {
      notifyError('Upload failed', 'Could not upload this file. Try again.');
      clear();
    } finally {
      setIsUploading(false);
      onBusyChange?.(false);
      setUploadLabel('Uploading…');
    }
  };

  const resetDrag = () => {
    dragDepth.current = 0;
    setDragging(false);
  };

  const onDragEnter = (e: React.DragEvent) => {
    if (!isFileDrag(e) || busy) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    setDragging(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };

  const onDragOver = (e: React.DragEvent) => {
    if (!isFileDrag(e) || busy) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resetDrag();
    if (busy) return;
    void handleFile(e.dataTransfer.files?.[0]);
  };

  const setDuration = (raw: string | number) => {
    clearError?.('customDuration');
    if (raw === '') {
      setCampaignData?.((prev) => ({ ...prev, customDuration: '' }));
      return;
    }
    const num = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isNaN(num) || num < 0) return;
    const val = num > MAX_AD_DURATION ? String(MAX_AD_DURATION) : String(raw);
    setCampaignData?.((prev) => ({ ...prev, customDuration: val }));
  };

  const bumpDuration = (delta: number) => {
    const current = Number(campaignData.customDuration) || MIN_AD_DURATION;
    setDuration(clampDuration(current + delta));
  };

  const resolvedPreview = resolveCreativePreview(creativePreview, campaignData);
  const previewUrl = resolvedPreview.url;
  const previewType = resolvedPreview.type;
  const orientation = String(
    campaignData.mediaIds?.[0]?.orientation || '',
  ).toUpperCase();
  const durationValue = campaignData.customDuration || '';
  const durationNum = Number(durationValue) || 0;
  const fromGallery = Boolean(
    campaignData.galleryId || campaignData.mediaIds?.[0]?.galleryId,
  );
  const isVideo = Boolean(
    previewType?.startsWith('video') ||
      campaignData.mediaType?.startsWith('video'),
  );

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.heic,.heif,.mp4,.mov,.m4v,.webm,.avi,.mkv"
      className="hidden"
      disabled={busy}
      onChange={(e) => {
        void handleFile(e.target.files?.[0]);
        e.target.value = '';
      }}
    />
  );

  const durationBlock =
    !hideDuration ? (
      <div className={`flex flex-col text-left ${compact ? 'gap-1.5' : 'gap-2'}`}>
        <div className="flex items-center justify-between gap-2">
          <label className="text-[13px] font-semibold tracking-[-0.01em] text-[var(--dash-ink)]">
            Ad duration
          </label>
          <span className="text-[11px] font-medium tabular-nums text-[var(--dash-muted)]">
            {MIN_AD_DURATION}–{MAX_AD_DURATION}s
          </span>
        </div>

        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Duration presets">
          {DURATION_PRESETS.map((sec) => {
            const active = durationNum === sec;
            return (
              <button
                key={sec}
                type="button"
                disabled={busy}
                onClick={() => setDuration(sec)}
                className={`rounded-full px-3 py-1.5 text-[12px] font-semibold transition active:scale-[0.97] disabled:opacity-50 ${
                  active
                    ? 'bg-[var(--dash-brand)] text-white shadow-[0_2px_8px_-2px_rgba(41,121,255,0.45)]'
                    : 'bg-[#EEF1F5] text-[var(--dash-ink)] hover:bg-[#E4E8EF]'
                }`}
              >
                {sec}s
              </button>
            );
          })}
        </div>

        <div
          className={`mb-creative-stepper flex items-center gap-2 rounded-[14px] border bg-[#F7F8FA] px-2 py-1.5 ${
            error?.toLowerCase().includes('duration')
              ? 'border-red-300 bg-red-50/40'
              : 'border-black/[0.08]'
          }`}
        >
          <button
            type="button"
            aria-label="Decrease duration"
            disabled={busy || durationNum <= MIN_AD_DURATION}
            onClick={() => bumpDuration(-5)}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-[var(--dash-ink)] shadow-[0_1px_2px_rgba(16,38,73,0.08)] ring-1 ring-black/[0.04] transition hover:bg-[#F2F4F7] disabled:opacity-40"
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <div className="flex min-w-0 flex-1 items-baseline justify-center gap-1">
            <input
              type="number"
              inputMode="numeric"
              min={MIN_AD_DURATION}
              max={MAX_AD_DURATION}
              aria-label="Ad duration in seconds"
              value={durationValue}
              disabled={busy}
              onKeyDown={(e) => {
                if (
                  e.key === '-' ||
                  e.key === 'e' ||
                  e.key === 'E' ||
                  e.key === '+' ||
                  e.key === '.'
                ) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => setDuration(e.target.value)}
              onBlur={() => {
                if (durationValue === '') return;
                setDuration(clampDuration(Number(durationValue) || MIN_AD_DURATION));
              }}
              onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
              className="w-16 border-0 bg-transparent text-center text-[22px] font-semibold tracking-[-0.03em] text-[var(--dash-ink)] outline-none tabular-nums disabled:opacity-60"
            />
            <span className="text-[13px] font-semibold text-[var(--dash-muted)]">
              sec
            </span>
          </div>
          <button
            type="button"
            aria-label="Increase duration"
            disabled={busy || durationNum >= MAX_AD_DURATION}
            onClick={() => bumpDuration(5)}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-[var(--dash-ink)] shadow-[0_1px_2px_rgba(16,38,73,0.08)] ring-1 ring-black/[0.04] transition hover:bg-[#F2F4F7] disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>

        {!compact ? (
          <p className="m-0 text-[11px] leading-snug text-[var(--dash-muted)]">
            Spot length on screen. Videos often lock to media length after upload.
          </p>
        ) : null}
      </div>
    ) : null;

  return (
    <div className="flex w-full flex-col gap-3 text-left">
      {!compact ? (
        <div className="flex items-center justify-between gap-3">
          <div>
            <label className="text-[13px] font-semibold tracking-[-0.01em] text-[var(--dash-ink)]">
              Creative
            </label>
            <p className="m-0 mt-0.5 text-[11px] text-[var(--dash-muted)]">
              Image or video · preview plays on the live display
            </p>
          </div>
          <div className="relative">
            <button
              type="button"
              aria-label="Creative upload info"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[var(--dash-muted)] transition hover:bg-black/[0.04] hover:text-[var(--dash-ink)]"
              onClick={() => setShowCreativeFormatInfo((v) => !v)}
              onBlur={() => setShowCreativeFormatInfo(false)}
            >
              <Info className="h-3.5 w-3.5" />
            </button>
            {showCreativeFormatInfo ? (
              <div className="absolute bottom-full right-0 z-50 mb-2 w-64 rounded-xl bg-[#0F1817] px-3 py-2.5 text-xs leading-relaxed text-white shadow-lg">
                Supported formats:
                <div className="mt-1 font-semibold text-white/95">
                  MP4, JPG, PNG · Images ≤25MB · Videos ≤100MB
                </div>
                <span className="absolute right-5 top-full -translate-y-1/2 border-4 border-transparent border-t-[#0F1817]" />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {busy ? (
        <div
          className="flex min-h-[148px] w-full flex-col items-center justify-center gap-2.5 rounded-[16px] border border-[var(--dash-brand)]/25 bg-[var(--dash-brand)]/[0.04] px-4 py-8"
          aria-busy
          aria-live="polite"
        >
          <Loader2 className="h-7 w-7 animate-spin text-[var(--dash-brand)]" />
          <span className="text-[13px] font-semibold tracking-[-0.01em] text-[var(--dash-ink)]">
            {uploadLabel}
          </span>
          <span className="text-[11px] text-[var(--dash-muted)]">
            Live preview updates when upload finishes
          </span>
        </div>
      ) : previewUrl && hidePreviewMedia ? (
        <div
          className={`mb-creative-active overflow-hidden rounded-[16px] border bg-white transition ${
            dragging
              ? 'border-[var(--dash-brand)] border-dashed bg-[var(--dash-brand)]/[0.04]'
              : 'border-black/[0.08] shadow-[0_1px_2px_rgba(16,38,73,0.04)]'
          }`}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
        >
          <div className="flex items-center gap-3 p-2.5 sm:p-3">
            <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-[12px] bg-[#0B1220] ring-1 ring-black/[0.06]">
              <ContentPreviewMedia
                url={previewUrl}
                type={previewType}
                thumb
                className="h-full w-full object-cover"
              />
              <span className="absolute bottom-1 left-1 rounded-md bg-black/55 px-1 py-px text-[8px] font-bold uppercase tracking-wide text-white">
                {isVideo ? 'Video' : 'Image'}
              </span>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white">
                  <Check className="h-2.5 w-2.5 stroke-[3]" />
                </span>
                <p className="m-0 truncate text-[13px] font-semibold tracking-[-0.01em] text-[var(--dash-ink)]">
                  Ready on live preview
                </p>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {orientation ? (
                  <span className="rounded-full bg-[#EEF1F5] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--dash-ink)]">
                    {orientation}
                  </span>
                ) : null}
                <span className="rounded-full bg-[#EEF1F5] px-2 py-0.5 text-[10px] font-semibold tabular-nums text-[var(--dash-ink)]">
                  {campaignData.customDuration || '30'}s
                </span>
                {fromGallery ? (
                  <span className="rounded-full bg-[var(--dash-brand)]/10 px-2 py-0.5 text-[10px] font-semibold text-[var(--dash-brand)]">
                    Gallery
                  </span>
                ) : null}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1">
              {enableGallery ? (
                <button
                  type="button"
                  onClick={() => setGalleryOpen(true)}
                  className="rounded-full border border-black/[0.08] bg-white px-2.5 py-1.5 text-[11px] font-semibold text-[var(--dash-ink)] transition hover:bg-[#F7F8FA]"
                >
                  Gallery
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="rounded-full border border-black/[0.08] bg-white px-2.5 py-1.5 text-[11px] font-semibold text-[var(--dash-ink)] transition hover:bg-[#F7F8FA]"
              >
                Replace
              </button>
              <button
                type="button"
                onClick={clear}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--dash-muted)] transition hover:bg-red-50 hover:text-red-600"
                aria-label="Remove content"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          {dragging ? (
            <div className="border-t border-[var(--dash-brand)]/20 px-3 py-2 text-center text-[11px] font-semibold text-[var(--dash-brand)]">
              Drop to replace creative
            </div>
          ) : null}
        </div>
      ) : previewUrl ? (
        <div
          className={`relative ${compact ? 'min-h-[112px]' : 'min-h-[160px]'} overflow-hidden rounded-[16px] border bg-[var(--dash-ink)]/90 transition ${
            dragging
              ? 'border-2 border-dashed border-[var(--dash-brand)] bg-[var(--dash-brand)]/10'
              : 'border-black/[0.08]'
          }`}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
        >
          <ContentPreviewMedia
            url={previewUrl}
            type={previewType}
            className={`h-full w-full object-contain ${compact ? 'min-h-[112px] max-h-36' : 'min-h-[160px] max-h-64'}`}
          />
          {dragging ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[var(--dash-brand)]/10">
              <span className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-[var(--dash-brand)] shadow">
                Drop to replace
              </span>
            </div>
          ) : null}
          <div className="absolute top-3 right-3 flex items-center gap-2">
            {enableGallery ? (
              <button
                type="button"
                onClick={() => setGalleryOpen(true)}
                className="rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-gray-700 shadow backdrop-blur-sm"
              >
                Gallery
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-gray-700 shadow backdrop-blur-sm"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={clear}
              className="rounded-full bg-white/90 p-2 shadow backdrop-blur-sm"
              aria-label="Remove content"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
          className={`flex min-h-[156px] w-full flex-col items-center justify-center gap-3 rounded-[16px] border-2 border-dashed px-4 py-7 transition select-none ${
            dragging
              ? 'border-[var(--dash-brand)] bg-[var(--dash-brand)]/[0.06]'
              : error
                ? 'border-red-300 bg-red-50/40'
                : 'border-black/[0.1] bg-[#F7F8FA] hover:border-black/[0.16] hover:bg-[#F2F4F7]'
          }`}
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-[0_1px_3px_rgba(16,38,73,0.1)] ring-1 ring-black/[0.04]">
            <Upload className="h-4 w-4 text-[var(--dash-brand)]" />
          </span>
          <div className="text-center">
            <p className="m-0 text-[13px] font-semibold tracking-[-0.01em] text-[var(--dash-ink)]">
              {dragging ? 'Drop to upload' : 'Add campaign creative'}
            </p>
            <p className="m-0 mt-0.5 text-[11px] text-[var(--dash-muted)]">
              Drag & drop · Images ≤25MB · Videos ≤100MB
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-full bg-[var(--dash-brand)] px-3.5 py-2 text-[12px] font-semibold text-white shadow-[0_4px_12px_-4px_rgba(41,121,255,0.5)] transition hover:opacity-95 active:scale-[0.98] disabled:opacity-50"
            >
              <Upload className="h-3.5 w-3.5" />
              Upload
            </button>
            {enableGallery ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => setGalleryOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full border border-black/[0.08] bg-white px-3.5 py-2 text-[12px] font-semibold text-[var(--dash-ink)] transition hover:bg-[#F7F8FA] active:scale-[0.98] disabled:opacity-50"
              >
                <Images className="h-3.5 w-3.5" />
                Gallery
              </button>
            ) : null}
          </div>
        </div>
      )}

      {fileInput}
      {durationBlock}
      {error ? (
        <p className="m-0 text-xs font-medium text-red-500" role="alert">
          {error}
        </p>
      ) : null}

      {enableGallery ? (
        <GalleryPickerModal
          open={galleryOpen}
          onClose={() => setGalleryOpen(false)}
          onSelect={handleGallerySelect}
        />
      ) : null}
    </div>
  );
}
